import { noSubject } from 'owa-locstrings/lib/strings/nosubject.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47781 */
import MailListItemExpansionLoadMoreButton from './MailListItemExpansionLoadMoreButton';
import MailListItemPart from './MailListItemPart';
import { observer } from 'mobx-react-lite';
import { ClientItemId } from 'owa-client-ids';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { BusStopState } from 'owa-mail-list-store';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import isNewestOnBottom from 'owa-mail-reading-pane-store/lib/utils/isNewestOnBottom';
import ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import mailStore from 'owa-mail-store/lib/store/Store';
import canConversationLoadMore from 'owa-mail-store/lib/utils/canConversationLoadMore';
import { getItemToShowFromNodeId } from 'owa-mail-store/lib/utils/conversationsUtils';
import { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import DisplayDensityMode from 'owa-service/lib/contract/DisplayDensityMode';

import * as React from 'react';
import {
    MAIL_ITEM_DRAG_YOFFSET,
    MAIL_ITEM_DRAG_XOFFSET,
    getMailListItemPartDragPreview,
} from '../utils/mailListDragUtil';

const styles = require('./MailListItemPart.scss');

export interface MailListItemExpansionProps {
    conversationId: string;
    tableViewId: string;
    isSingleLine: boolean;
    tableViewSupportsFlagging: boolean;
    densityMode: DisplayDensityMode;
    styleSelectorAsPerUserSettings: string;
}

export default observer(function MailListItemExpansion(props: MailListItemExpansionProps) {
    const conversationItem = listViewStore.conversationItems.get(props.conversationId);
    const displayItemParts = conversationItem.globalMessageCount > 1;
    const conversationItemParts: ConversationItemParts = mailStore.conversations.get(
        conversationItem.id
    );

    // this can happen when the item parts have just all be deleted
    if (!displayItemParts || !conversationItemParts) {
        return null;
    }

    const isNewestItemsOnBottom = isNewestOnBottom();
    const conversationNodeIds = conversationItemParts.conversationNodeIds;

    // load more bus stop is shown if the root node is showing a bus end
    const busStopStateMap = listViewStore.expandedConversationViewState.busStopStateMap;
    const showLoadMoreBusStop = isNewestItemsOnBottom
        ? busStopStateMap.get(conversationNodeIds[0]) === BusStopState.BusEnd
        : busStopStateMap.get(conversationNodeIds[conversationNodeIds.length - 1]) ===
          BusStopState.BusEnd;

    const loadMoreButton =
        canConversationLoadMore(conversationItem.id) &&
        renderLoadMoreButton(
            conversationItem.clientId,
            conversationItemParts.isLoadMoreInProgress,
            showLoadMoreBusStop,
            props.isSingleLine
        );

    // The position of the load more button has 4 possibe cases:
    // 1. On the top of the list: if this conversation doesn't have root node and it's newest on bottom.
    // 2. Under the first item: if this conversation has root node and it's newest on bottom.
    // 3. Before the last item: if this conversation has root node and it's newest on top.
    // 4. On the bottom of the list: if this conversation doesn't have root node and it's newest on top.

    return (
        <div className={styles.expansionContainer}>
            <Draggable
                canDrag={canDrag}
                getDragData={() => getMailListExpansionDragData(props.tableViewId)}
                getDragPreview={getMailListItemPartDragPreview}
                xOffset={MAIL_ITEM_DRAG_XOFFSET}
                yOffset={MAIL_ITEM_DRAG_YOFFSET}>
                {conversationNodeIds.length > 0 &&
                    conversationNodeIds.map((nodeId, index) => {
                        const mailListItemPart = (
                            <MailListItemPart
                                key={nodeId}
                                allNodeIds={conversationNodeIds}
                                nodeId={nodeId}
                                tableViewId={props.tableViewId}
                                isSingleLine={props.isSingleLine}
                                tableViewSupportsFlagging={props.tableViewSupportsFlagging}
                                densityMode={props.densityMode}
                                styleSelectorAsPerUserSettings={
                                    props.styleSelectorAsPerUserSettings
                                }
                            />
                        );

                        if (isNewestItemsOnBottom && index == 0) {
                            return (
                                <>
                                    {mailListItemPart}
                                    {loadMoreButton}
                                </>
                            );
                        } else if (
                            !isNewestItemsOnBottom &&
                            index == conversationNodeIds.length - 1
                        ) {
                            return (
                                <>
                                    {loadMoreButton}
                                    {mailListItemPart}
                                </>
                            );
                        }
                        return mailListItemPart;
                    })}
            </Draggable>
        </div>
    );
});

const canDrag = () => {
    return listViewStore.expandedConversationViewState.selectedNodeIds.length > 0;
};

const getMailListExpansionDragData = (tableViewId: string): MailListItemPartDragData => {
    // Also rename this to getMailListRowDragData. May be get this information from the mailRowData getter api
    const itemIds = [];
    const subjects = [];
    const sizes = [];
    const selectedNodeIds = listViewStore.expandedConversationViewState.selectedNodeIds;
    selectedNodeIds.forEach(nodeId => {
        const item = getItemToShowFromNodeId(nodeId);
        if (item) {
            subjects.push(item.Subject ? item.Subject : loc(noSubject));
            itemIds.push(item.ItemId.Id);
            sizes.push(item.Size);
        }
    });

    return {
        itemType: DraggableItemTypes.MailListItemPart,
        tableViewId: tableViewId,
        subjects: subjects,
        itemIds: itemIds,
        nodeIds: selectedNodeIds,
        sizes: sizes,
    };
};

const renderLoadMoreButton = (
    conversationId: ClientItemId,
    isLoadMoreInProgress: boolean,
    showBusStop: boolean,
    isSingleLine: boolean
): JSX.Element => {
    return (
        <MailListItemExpansionLoadMoreButton
            conversationId={conversationId}
            isLoadMoreInProgress={isLoadMoreInProgress}
            isSingleLineView={isSingleLine}
            showBusStop={showBusStop}
        />
    );
};
